<!--
	AppWindow — the instrument on the desk, and the load-bearing device of the whole
	design. Every real render from the Volvo job is shown INSIDE this: a desktop
	window with a title bar, a tool rail, an inspector and a status bar.

	Why it exists: a bare screenshot on a marketing page reads as a picture OF
	software. Chrome around it reads as software. The chrome is honest — the tools
	are the six real pipeline steps, the inspector shows the real measured figures
	from content/hero.ts, and the file name is the actual part.

	The window surface is `--color-app-950`, which is the native background of the
	`real-*.webp` renders, so an <img> dropped in the canvas has no seam.

	Composition: `canvas` is the only required snippet. The rails are data-driven
	(`tools`, `inspector`, `status`) rather than snippets, because every caller wants
	the same shape and the point is that it stays consistent down the page.
-->
<script lang="ts">
	import type { Snippet } from 'svelte';
	import ToolGlyph, { type GlyphName } from './ToolGlyph.svelte';

	export interface WindowTool {
		glyph: GlyphName;
		/** Accessible name, and the tooltip an installer would read. */
		label: string;
	}
	export interface InspectorRow {
		label: string;
		value: string;
		/** Live/measured data gets the cut accent. Prose does not. */
		accent?: boolean;
	}

	let {
		title,
		/** Right side of the title bar — a scale, a mode, a part number. */
		subtitle,
		tools = [],
		/** Index into `tools` that's lit. Drives the rail highlight. */
		activeTool = -1,
		inspector = [],
		inspectorTitle = 'Inspector',
		status = [],
		canvas,
		/** Overlay drawn above the canvas, inside the window (progress dots, tabs). */
		overlay,
		/** Aspect ratio for the canvas area, e.g. '16/9'. Reserves space → no CLS. */
		ratio = '16/10',
		/**
		 * `contain` is the default and is almost always right: the real renders range
		 * from 1.8:1 to 2.65:1, and `cover` would crop the ends off a long panel —
		 * i.e. crop away the product. They sit on the same graphite as the canvas, so
		 * letterboxing is invisible. Use `cover` only for a shot you've framed yourself.
		 */
		fit = 'contain',
		class: klass = ''
	}: {
		title: string;
		subtitle?: string;
		tools?: WindowTool[];
		activeTool?: number;
		inspector?: InspectorRow[];
		inspectorTitle?: string;
		status?: string[];
		canvas: Snippet;
		overlay?: Snippet;
		ratio?: string;
		fit?: 'cover' | 'contain';
		class?: string;
	} = $props();
</script>

<div class="win app-surface {klass}">
	<!-- Title bar -->
	<div class="win__bar">
		<div class="win__lights" aria-hidden="true">
			<span></span><span></span><span></span>
		</div>
		<p class="win__title">{title}</p>
		{#if subtitle}
			<p class="win__subtitle">{subtitle}</p>
		{/if}
	</div>

	<div class="win__body">
		<!-- Tool rail -->
		{#if tools.length}
			<div class="win__rail" role="toolbar" aria-label="Pipeline tools" aria-orientation="vertical">
				{#each tools as tool, i (tool.label)}
					<span
						class="win__tool"
						class:win__tool--on={i === activeTool}
						role="img"
						title={tool.label}
						aria-label={tool.label}
						aria-current={i === activeTool ? 'step' : undefined}
					>
						<ToolGlyph name={tool.glyph} />
					</span>
				{/each}
			</div>
		{/if}

		<!-- Canvas -->
		<div class="win__canvas" style:aspect-ratio={ratio} style:--fit={fit}>
			{@render canvas()}
			{#if overlay}
				<div class="win__overlay">{@render overlay()}</div>
			{/if}
		</div>

		<!-- Inspector -->
		{#if inspector.length}
			<div class="win__inspector">
				<p class="win__inspector-title">{inspectorTitle}</p>
				<dl class="win__rows">
					{#each inspector as row (row.label)}
						<div class="win__row">
							<dt>{row.label}</dt>
							<dd class:win__val--accent={row.accent}>{row.value}</dd>
						</div>
					{/each}
				</dl>
			</div>
		{/if}
	</div>

	<!-- Status bar -->
	{#if status.length}
		<div class="win__status">
			{#each status as item, i (item)}
				{#if i > 0}<span class="win__dot" aria-hidden="true"></span>{/if}
				<span>{item}</span>
			{/each}
		</div>
	{/if}
</div>

<style>
	.win {
		border-radius: var(--radius-win);
		box-shadow: var(--shadow-win);
		/* The lit lip: a real window has a bright top edge and a dark bottom one. */
		box-shadow:
			inset 0 1px 0 rgba(255, 255, 255, 0.06),
			var(--shadow-win);
		overflow: hidden;
		container-type: inline-size;
	}

	/* ── Title bar ─────────────────────────────────────────────────────── */
	.win__bar {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		height: 2.25rem;
		padding-inline: 0.75rem;
		background-color: var(--color-app-900);
		border-bottom: 1px solid var(--color-app-700);
	}
	.win__lights {
		display: flex;
		gap: 0.375rem;
		flex: none;
	}
	.win__lights span {
		width: 0.625rem;
		height: 0.625rem;
		border-radius: 50%;
		background-color: var(--color-app-700);
	}
	.win__title,
	.win__subtitle {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.win__title {
		color: var(--color-bone-300);
		/* Centre it the way a title bar does, without the lights shoving it. */
		flex: 1;
		text-align: center;
	}
	.win__subtitle {
		color: var(--color-app-500);
		flex: none;
	}
	/* The lights are ~54px; balance the title by reserving the same on the right. */
	.win__bar:not(:has(.win__subtitle)) .win__title {
		margin-right: 3.375rem;
	}

	/* ── Body ──────────────────────────────────────────────────────────── */
	.win__body {
		display: flex;
		align-items: stretch;
		min-height: 0;
	}

	.win__rail {
		display: flex;
		flex-direction: column;
		gap: 0.25rem;
		flex: none;
		padding: 0.5rem 0.375rem;
		background-color: var(--color-app-900);
		border-right: 1px solid var(--color-app-700);
	}
	.win__tool {
		display: grid;
		place-items: center;
		width: 1.75rem;
		height: 1.75rem;
		border-radius: var(--radius-sm);
		color: var(--color-app-500);
		transition:
			color var(--dur-med) var(--ease-out),
			background-color var(--dur-med) var(--ease-out);
	}
	/* The lit tool: the accent earns its place here — this IS live state. */
	.win__tool--on {
		color: var(--color-cut-400);
		background-color: color-mix(in srgb, var(--color-cut-400) 12%, transparent);
	}

	.win__canvas {
		position: relative;
		flex: 1;
		min-width: 0;
		background-color: var(--color-app-950);
		overflow: hidden;
	}
	.win__canvas :global(img),
	.win__canvas :global(video),
	.win__canvas :global(canvas) {
		width: 100%;
		height: 100%;
		object-fit: var(--fit, contain);
		display: block;
	}
	.win__overlay {
		position: absolute;
		inset: 0;
		pointer-events: none;
	}
	/* Anything interactive an overlay puts up must still be clickable. */
	.win__overlay :global(button),
	.win__overlay :global(a) {
		pointer-events: auto;
	}

	.win__inspector {
		flex: none;
		width: 11.5rem;
		padding: 0.75rem;
		background-color: var(--color-app-900);
		border-left: 1px solid var(--color-app-700);
	}
	.win__inspector-title {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-app-500);
		padding-bottom: 0.5rem;
		margin-bottom: 0.5rem;
		border-bottom: 1px solid var(--color-app-700);
	}
	.win__rows {
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}
	.win__row {
		display: flex;
		flex-direction: column;
		gap: 0.125rem;
	}
	.win__row dt {
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		color: var(--color-app-500);
	}
	.win__row dd {
		font-family: var(--font-mono);
		font-size: 0.75rem;
		font-variant-numeric: tabular-nums;
		color: var(--color-bone-300);
	}
	.win__val--accent {
		color: var(--color-cut-400);
	}

	/* ── Status bar ────────────────────────────────────────────────────── */
	.win__status {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		height: 1.75rem;
		padding-inline: 0.75rem;
		background-color: var(--color-app-900);
		border-top: 1px solid var(--color-app-700);
		font-family: var(--font-mono);
		font-size: var(--text-mono-xs);
		letter-spacing: 0.06em;
		color: var(--color-app-500);
		white-space: nowrap;
		overflow: hidden;
	}
	.win__status > span {
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
	}
	.win__dot {
		width: 2px;
		height: 2px;
		border-radius: 50%;
		background-color: currentColor;
		flex: none;
	}

	/* ── Narrow: the rails are the first thing a real app collapses ───────
	   Container queries, not viewport — the window is used at several widths, and
	   what has to give depends on the window, not the page.

	   The status bar sheds items in two stages. Children alternate text/dot/text,
	   so `n + 5` drops the third item and `n + 2` leaves only the first. Without
	   the second stage a phone-width window clips the remaining text mid-word
	   ("23,849 f"), which reads as broken rather than truncated; the survivor
	   ellipses instead. */
	@container (max-width: 34rem) {
		.win__inspector {
			display: none;
		}
		.win__status > :nth-child(n + 5) {
			display: none;
		}
	}
	@container (max-width: 26rem) {
		.win__status > :nth-child(n + 2) {
			display: none;
		}
	}
	@container (max-width: 22rem) {
		.win__rail {
			display: none;
		}
	}
</style>
